import { Typography } from 'antd'
import { AlertTriangle, Info, ShieldAlert, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/router'

import { useStyles } from './styles'
import type { AttentionItem, AttentionSection } from './types'

const { Text } = Typography

const SEVERITY: Record<
  AttentionItem['severity'],
  { icon: LucideIcon; color: string }
> = {
  error: { icon: AlertTriangle, color: 'var(--ant-color-error)' },
  warn: { icon: ShieldAlert, color: 'var(--ant-color-warning)' },
  info: { icon: Info, color: 'var(--ant-color-info)' }
}

type Props = { items: AttentionItem[]; workspaceIdParam: string }

export const AttentionLane = ({ items, workspaceIdParam }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const routeFor = (section: AttentionSection): string => {
    switch (section) {
      case 'billing':
        return paths.workspaces.billing(workspaceIdParam)
      case 'security':
        return paths.workspaces.security(workspaceIdParam)
      case 'audit':
        return paths.workspaces.audit(workspaceIdParam)
      case 'apps':
        return paths.workspaces.apps.root(workspaceIdParam)
      case 'agents':
        return paths.workspaces.agents(workspaceIdParam)
      default:
        return paths.workspaces.details(workspaceIdParam)
    }
  }

  return (
    <div className={styles.attentionLane}>
      <span className={styles.attentionHead}>
        {t('workspaceOverview.companyGraph.needsAttention')}
        {items.length > 0 ? (
          <span className={styles.attentionCount}>{items.length}</span>
        ) : null}
      </span>

      {items.length === 0 ? (
        <Text className={styles.allClear}>
          {t('workspaceOverview.companyGraph.allClear')}
        </Text>
      ) : (
        items.map((item) => {
          const sev = SEVERITY[item.severity]
          const Icon = sev.icon

          return (
            <div
              key={item.id}
              className={styles.attentionItem}
              role="button"
              tabIndex={0}
              onClick={() => navigate(routeFor(item.section))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate(routeFor(item.section))
                }
              }}
            >
              <span
                className={styles.attentionIcon}
                style={{ color: sev.color }}
              >
                <Icon size={15} />
              </span>
              <span className={styles.attentionText}>
                <Text className={styles.attentionTitle}>{item.title}</Text>
                <Text className={styles.attentionMeta}>{item.meta}</Text>
              </span>
            </div>
          )
        })
      )}
    </div>
  )
}
